import React from "react";
import { Recycle, Building2, TrendingUp, ArrowRight } from "lucide-react";

/**
 * DGCompareCard — Iter135
 * Compara KPIs de Residuos (TRATAMIENTO) vs Servicios Urbanos (SERVICIOS)
 * en el Dashboard. Muestra dos columnas con métricas clave y una barra
 * indicadora del reparto proporcional.
 *
 * Props:
 *   - metrics: [{ key, label, tra, ser, formatter?, hint? }]
 *   - onClose: () => void
 *
 *  `tra` = valor Residuos ; `ser` = valor Servicios Urbanos.
 *  El componente NO calcula nada; recibe los valores ya derivados.
 */
export default function DGCompareCard({ metrics = [], onClose, className = "" }) {
  return (
    <div
      className={`bg-white rounded-2xl border border-slate-200 shadow-sm p-5 ${className}`}
      data-testid="dg-compare-card"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Comparar DGs</div>
          <h3 className="font-semibold text-slate-900 text-base mt-0.5">Residuos vs Servicios Urbanos</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">KPIs lado a lado con el reparto proporcional del pipeline actual.</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            data-testid="dg-compare-close"
            className="text-xs text-slate-400 hover:text-slate-700 border border-slate-200 hover:border-slate-300 rounded-full px-3 py-1">
            Salir de la comparativa
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 p-3">
          <div className="flex items-center gap-1.5 text-emerald-800 text-[11px] font-bold tracking-wide">
            <Recycle size={14}/> RESIDUOS
          </div>
          <div className="text-[10px] text-emerald-600 mt-0.5">TRATAMIENTO</div>
        </div>
        <div className="rounded-xl bg-gradient-to-br from-blue-50 to-sky-50 border border-blue-200 p-3">
          <div className="flex items-center gap-1.5 text-blue-800 text-[11px] font-bold tracking-wide">
            <Building2 size={14}/> SERVICIOS URBANOS
          </div>
          <div className="text-[10px] text-blue-600 mt-0.5">SERVICIOS</div>
        </div>
      </div>

      {metrics.length === 0 ? (
        <div className="text-center text-slate-400 text-xs py-4">Sin métricas para comparar.</div>
      ) : (
        <div className="space-y-2.5">
          {metrics.map(m => {
            const fmt = m.formatter || (v => (v ?? 0).toLocaleString("es-ES"));
            const tra = Number(m.tra || 0);
            const ser = Number(m.ser || 0);
            const total = tra + ser;
            const traPct = total > 0 ? (tra / total) * 100 : 50;
            const serPct = total > 0 ? (ser / total) * 100 : 50;
            const leader =
              total === 0 ? null : tra === ser ? "tie" : tra > ser ? "tra" : "ser";
            return (
              <div
                key={m.key}
                className="border border-slate-100 rounded-xl p-3"
                data-testid={`dg-compare-metric-${m.key}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">{m.label}</div>
                  {leader && leader !== "tie" && (
                    <div className={`text-[10px] font-semibold flex items-center gap-1 ${leader === "tra" ? "text-emerald-700" : "text-blue-700"}`}>
                      <TrendingUp size={10}/> {leader === "tra" ? "Residuos +" : "Serv. Urb. +"}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 items-baseline">
                  <div className="text-emerald-800">
                    <span className="text-xl font-bold" data-testid={`dg-compare-${m.key}-tra`}>{fmt(tra)}</span>
                  </div>
                  <div className="text-right text-blue-800">
                    <span className="text-xl font-bold" data-testid={`dg-compare-${m.key}-ser`}>{fmt(ser)}</span>
                  </div>
                </div>
                {/* Barra proporcional */}
                <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden flex" title={`Residuos ${traPct.toFixed(0)}% · Servicios Urbanos ${serPct.toFixed(0)}%`}>
                  <div className="bg-emerald-500" style={{width: `${traPct}%`}}/>
                  <div className="bg-blue-500"     style={{width: `${serPct}%`}}/>
                </div>
                {m.hint && (
                  <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                    <ArrowRight size={10}/> {m.hint}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
